import { Fragment, RefObject, useCallback, useMemo } from 'react'

import { useRowsVirtualization } from '../../DataGrid.hooks'
import {
  CellClickArgs,
  ColumnConfig,
  GroupByCellRenderer,
  GroupByRenderer,
  VirtualizationConfig,
} from '../../DataGrid.types'
import { genericMemo, getColumnValue, StickyController } from '../../utils'
import { RowCells } from '../RowCells'
import { RowsGroupHeaderRow } from '../RowsGroupHeaderRow'
import * as S from './Rows.styles'

interface RowsProps<RowType> {
  columns: ColumnConfig<RowType>[]
  data: RowType[]
  gridId: string
  groundByRenderer?: GroupByRenderer
  groupByCellRenderer?: GroupByCellRenderer
  groupByField?: keyof RowType
  onCellClick?: (cellClickArgs: CellClickArgs<RowType>) => void
  pinnedColumns: number
  pinnedColumnsRight: number
  selectedRowIndexes?: number[]
  selectedColumnIndexes?: number[]
  skipFromGroupingField?: keyof RowType
  stickyControllerRef: RefObject<StickyController>
  virtualization?: VirtualizationConfig
}
function _Rows<RowType>(props: RowsProps<RowType>) {
  const {
    columns,
    data,
    gridId,
    groundByRenderer,
    groupByCellRenderer,
    groupByField,
    onCellClick,
    pinnedColumns,
    pinnedColumnsRight,
    selectedRowIndexes,
    selectedColumnIndexes,
    skipFromGroupingField,
    stickyControllerRef,
    virtualization,
  } = props

  const shouldRenderRowGroupHeader = useCallback(
    (rowData: RowType, rowIndex: number) =>
      groupByField &&
      (!skipFromGroupingField || !rowData[skipFromGroupingField]) &&
      (rowIndex === 0 || data[rowIndex - 1]?.[groupByField] !== rowData[groupByField]),
    [groupByField, skipFromGroupingField, data],
  )

  const renderRowGroupHeader = (rowData: RowType, rowIndex: number) => {
    if (groupByField && shouldRenderRowGroupHeader(rowData, rowIndex)) {
      return (
        <RowsGroupHeaderRow
          columns={columns}
          groundByRenderer={groundByRenderer}
          groupByCellRenderer={groupByCellRenderer}
          groupByField={groupByField}
          pinnedColumns={pinnedColumns}
          pinnedColumnsRight={pinnedColumnsRight}
          rowData={rowData}
          stickyControllerRef={stickyControllerRef}
        />
      )
    }

    return null
  }

  const groupBorders = useMemo(
    () =>
      groupByField &&
      data.reduce((groups, rowData, rowIndex) => {
        if (shouldRenderRowGroupHeader(rowData, rowIndex)) {
          groups.push(groups.length > 0 ? groups[groups.length - 1] + 1 : 1)
        } else {
          groups[groups.length - 1] += 1
        }
        return groups
      }, [] as number[]),
    [data, groupByField, shouldRenderRowGroupHeader],
  )

  const { rowsToRender } = useRowsVirtualization(gridId, [data], virtualization, groupBorders)

  let isEven = true
  let hasDifferentPreviousValue = true
  return data?.map((rowData, rowIndex) => {
    const groupHeader = renderRowGroupHeader(rowData, rowIndex)

    const groupRow = columns.find(column => column.shouldSpanRowWithSameValue)

    if (groupRow) {
      const previousValue = getColumnValue({
        columnKey: groupRow.columnKey,
        rowData: data[rowIndex - 1],
      })
      const value = getColumnValue({ columnKey: groupRow.columnKey, rowData })
      hasDifferentPreviousValue = previousValue !== value
    }

    if (hasDifferentPreviousValue || !!groupByField) {
      isEven = !groupHeader ? !isEven : false
    }

    const isSelected = selectedRowIndexes?.some(index => index === rowIndex)

    return (
      <Fragment key={`rowsMaybeGroup-${rowIndex}`}>
        {groupHeader}
        {virtualization && (rowIndex < rowsToRender.start || rowIndex > rowsToRender.end) ? (
          <S.RowPlaceholder height={virtualization.rowHeight} />
        ) : (
          <RowCells
            columns={columns}
            data={data}
            gridId={gridId}
            onCellClick={onCellClick}
            pinnedColumns={pinnedColumns}
            pinnedColumnsRight={pinnedColumnsRight}
            selectedColumnIndexes={selectedColumnIndexes}
            stickyControllerRef={stickyControllerRef}
            isSelected={isSelected}
            rowIndex={rowIndex}
            rowData={rowData}
            isEvenRow={isEven}
            hasDifferentPreviousValue={hasDifferentPreviousValue}
            isLastRow={rowIndex === data.length - 1}
          />
        )}
      </Fragment>
    )
  })
}

export const Rows = genericMemo(_Rows)
